import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Loader2, AlertCircle, ArrowLeft, MailCheck, User } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function RegisterCompany() {
  const [company, setCompany] = useState({
    ragione_sociale: "", vat_number: "", tax_code: "", sdi_code: "", pec: "", phone: "",
    billing_address: { street: "", cap: "", city: "", province: "", country: "IT" },
  });
  const [account, setAccount] = useState({ email: "", password: "" });
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setC = (k) => (e) => setCompany({ ...company, [k]: e.target.value });
  const setA = (k) => (e) => setCompany({ ...company, billing_address: { ...company.billing_address, [k]: e.target.value } });
  const setAcc = (k) => (e) => setAccount({ ...account, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) { setError("Devi accettare l'informativa privacy."); return; }
    if (account.password.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
    setLoading(true);
    try {
      await api.post("/public/register/company", {
        company: {
          ...company,
          vat_number: company.vat_number.trim() || null,
          tax_code: company.tax_code.trim() || null,
          sdi_code: company.sdi_code.trim() || null,
          pec: company.pec.trim() || null,
          phone: company.phone.trim() || null,
        },
        email: account.email,
        password: account.password,
        privacy_accepted: true,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-company-page" className="min-h-screen bg-brand-bg brand-grid-bg flex flex-col">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-16">
        <div className="w-full max-w-2xl">
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue text-white">
              <GraduationCap size={22} strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <div className="wordmark text-brand-blue text-xl">Buro Academy</div>
              <div className="text-xs text-brand-muted -mt-0.5">Burocratica Servizi</div>
            </div>
          </div>

          <div className="bg-white border border-brand-line rounded-sm shadow-sm p-8">
            <div className="gold-rule" />

            {done ? (
              <div data-testid="register-company-done">
                <div className="flex items-center gap-2 text-brand-blue mb-3">
                  <MailCheck size={22} />
                  <h1 className="text-2xl text-brand-ink">Controlla la tua email</h1>
                </div>
                <p className="text-sm text-brand-muted mb-6">
                  Abbiamo inviato al referente un'email per confermare l'indirizzo.
                  Dopo la conferma potrai accedere all'area azienda, registrare i
                  discenti e acquistare i corsi.
                </p>
                <Link to="/login" className="btn-primary w-full inline-flex items-center justify-center gap-2">
                  Vai all'accesso
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-2xl text-brand-ink mb-1">Registra la tua azienda</h1>
                <p className="text-sm text-brand-muted mb-6">
                  Crea l'account aziendale: potrai iscrivere i tuoi discenti e gestire
                  acquisti e fatturazione. Sei un privato?{" "}
                  <Link to="/registrati" className="text-brand-blue hover:underline underline-offset-4 inline-flex items-center gap-1">
                    <User size={13} /> Registrati come privato
                  </Link>
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-company-form">
                  <section>
                    <h2 className="text-sm font-semibold text-brand-ink mb-3 uppercase tracking-wide">Dati azienda</h2>
                    <div className="space-y-4">
                      <Field label="Ragione sociale" value={company.ragione_sociale} onChange={setC("ragione_sociale")} required />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Partita IVA" value={company.vat_number} onChange={setC("vat_number")} />
                        <Field label="Codice fiscale (se ditta individuale)" value={company.tax_code} onChange={setC("tax_code")} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Codice SDI" value={company.sdi_code} onChange={setC("sdi_code")} />
                        <Field label="PEC" value={company.pec} onChange={setC("pec")} />
                      </div>
                      <Field label="Indirizzo (via e civico)" value={company.billing_address.street} onChange={setA("street")} />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Field label="CAP" value={company.billing_address.cap} onChange={setA("cap")} />
                        <Field label="Città" value={company.billing_address.city} onChange={setA("city")} />
                        <Field label="Prov." value={company.billing_address.province} onChange={setA("province")} />
                        <Field label="Telefono" value={company.phone} onChange={setC("phone")} />
                      </div>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-sm font-semibold text-brand-ink mb-3 uppercase tracking-wide">Credenziali di accesso</h2>
                    <p className="text-xs text-brand-muted -mt-2 mb-3">
                      L'azienda accede al portale con questa email e la password.
                    </p>
                    <div className="space-y-4">
                      <Field label="Email" type="email" value={account.email} onChange={setAcc("email")} required />
                      <Field label="Password (min. 8 caratteri)" type="password" value={account.password} onChange={setAcc("password")} required minLength={8} />
                    </div>
                  </section>

                  <label className="flex items-start gap-2 text-sm text-brand-muted">
                    <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5" />
                    <span>
                      Ho letto e accetto l'
                      <Link to="/privacy" className="text-brand-blue hover:underline underline-offset-4">informativa privacy</Link>.
                    </span>
                  </label>

                  {error && (
                    <div role="alert" className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? (<><Loader2 size={18} className="animate-spin" /> Registrazione…</>) : "Registra l'azienda"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-brand-ink">{label}</Label>
      <Input {...props} className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue" />
    </div>
  );
}
